import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer, ProductsSd } from '@grandvisionhq/analytics';
import { useLocation } from '@grandvisionhq/frk-routing';
import { ProductListing } from '@grandvisionhq/products';
import { useIntl } from '@grandvisionhq/state';
import { useDispatchTrackEvent } from '@grandvisionhq/svc-tealium/events';
import { isArray } from '@grandvisionhq/utils';
import 'react-instantsearch';
import '../types.js';
import { useAlgoliaConfig } from '../Algolia/AlgoliaConfig.js';
import '@grandvisionhq/common';
import 'lodash/orderBy';
import '@grandvisionhq/forms';
import '@grandvisionhq/icons';
import 'classnames';
import 'lodash/uniqBy';
import '@grandvisionhq/elements';
import 'motion/react';
import 'algoliasearch/lite';
import 'react-dom/server';
import 'lodash/merge';
import '../Algolia/utils/query-string.js';
import { useAlgoliaData } from '../Algolia/AlgoliaDataProvider.js';
import '../Algolia/AlgoliaAnalytics.js';
import '../CrossSellProducts/utils/create-algolia-filters.js';
import { getForwardedQueryParamsFromFacets } from '../Algolia/utils/get-forwarded-query-params-from-facets.js';
import { useFacets } from '../hooks/use-facets.js';
import { ConnectedInfinitePagination } from '../InfinitePagination/InfinitePagination.js';
import { NoResults } from '../NoResults/NoResults.js';
import { mapFacetsToTealiumFacetValue } from '../Tealium/mappers/map-facets-to-tealium-facet-value.js';
import { createPagedHits } from './create-paged-hits.js';
import { useFormatProductLinks } from './hooks/use-format-product-links.js';

function ConnectedProductListing({ promotionBig , promotionSmall , reimbursableLogos  }) {
    const { getLabel  } = useIntl();
    const { pageSize , facets  } = useAlgoliaConfig();
    const { mappedResults: hits , total , searchState , isLoading , searchQuery  } = useAlgoliaData();
    const { query , configure , refinementList  } = searchState;
    const page = (configure === null || configure === void 0 ? void 0 : configure.page) || 1;
    const length = (configure === null || configure === void 0 ? void 0 : configure.length) ? Number(configure.length) : page * pageSize;
    const { pathname  } = useLocation();
    const dispatchTrackEvent = useDispatchTrackEvent();
    const { appliedFacets , numberOfAppliedFacets  } = useFacets();
    const previousNumberofAppliedFacets = React.useRef(numberOfAppliedFacets);
    const facetsWithOptionalSearchTerm = pathname.startsWith('/search') ? swcHelpers.objectSpread({}, appliedFacets, {
        q: {
            value: [
                searchQuery
            ],
            label: ''
        }
    }) : appliedFacets;
    React.useEffect(()=>{
        if (previousNumberofAppliedFacets.current !== numberOfAppliedFacets && !isLoading) {
            previousNumberofAppliedFacets.current = numberOfAppliedFacets;
            dispatchTrackEvent(()=>Promise.resolve({
                    id: 'Event',
                    Events_SearchFiltering: '1',
                    Search_ResultItemsQnt: `${total}`,
                    Search_FacetValues_String: mapFacetsToTealiumFacetValue(facetsWithOptionalSearchTerm)
                })
            );
        }
    /**
     * The event trigger only needs to be dependent on the appliedFacets.
     * Because the searchquery will only change on complete rerender of the page.
     */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        previousNumberofAppliedFacets,
        dispatchTrackEvent,
        appliedFacets,
        total,
        isLoading,
        numberOfAppliedFacets, 
    ]);
    /**
   * If there are any search parameters (pre-applied filters) in the URL,
   * page_data_loaded event will be dispatched to the DataLayer with those filters.
   */ React.useEffect(()=>{
        const filterKeys = Object.keys(facetsWithOptionalSearchTerm);
        const combinedFilterValues = Object.values(facetsWithOptionalSearchTerm).map((facet)=>isArray(facet.value) ? facet.value.join('-') : facet.value
        );
        DataLayer.pushEvent({
            event: 'page_data_loaded',
            page: {
                unixTimestamp: Math.floor(Date.now() / 1000),
                type: 'product listing',
                filter_type: filterKeys.join(', '),
                filter_value: combinedFilterValues.join(', ')
            }
        });
    // Only needs to be fired on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
   * Facets can be configured to be forwarded on PDP urls when they are active
   * for prefilling information on the PDP
   */ const forwardedQueryParams = getForwardedQueryParamsFromFacets(facets, refinementList);
    /**
   * Creates a paged object of the hits we got from algolia.
   * If the amount of hits is lower than the amount of items we should render according to
   * the page number provided trough the URL, it means we should fill the page with the amount
   * of pages that can't be rendered yet, because the server-sided, only the last page is fetched
   * The maximum number of previous pages is capped to the number of products divided by page size
   */ const pages = React.useMemo(()=>{
        const totalNumberOfPages = Math.ceil(total / pageSize);
        const fill = length > hits.length && hits.length !== total ? Math.min(totalNumberOfPages, page) - 1 : 0;
        return createPagedHits(hits, total, pageSize, fill);
    }, [
        hits,
        total,
        pageSize,
        page,
        length
    ]);
    const links = useFormatProductLinks({
        products: hits
    });
    if (hits.length === 0) {
        return /*#__PURE__*/ React.createElement(NoResults, {
            title: getLabel('search.noResults.title'),
            description: getLabel('search.noResults.text'),
            query: query
        });
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ProductListing, {
        products: pages,
        showMoreButton: /*#__PURE__*/ React.createElement(ConnectedInfinitePagination, null),
        promotionBig: promotionBig,
        promotionSmall: promotionSmall,
        reimbursableLogos: reimbursableLogos,
        forwardedQueryParams: forwardedQueryParams
    }), /*#__PURE__*/ React.createElement(ProductsSd, {
        items: hits,
        links: links
    }));
}

export { ConnectedProductListing };
//# sourceMappingURL=ConnectedProductListing.js.map
